import React, {useEffect, useState} from 'react';
import clsx from "clsx";
import {motion} from "framer-motion";
import Button from "../elements/Button/Button";
import styles from "../FormController/components/Form/Form.module.scss";
import changePasswordStyles from './ChangePassword.module.scss';
import {useNavigate} from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";
import {setErrors} from "../../store/actions/errorsActions";
import toast from "react-hot-toast";
import {AiOutlineEye, AiOutlineEyeInvisible} from "react-icons/ai";
import {changePassword as changePasswordAPI} from "../../services/user";
import formValidation from "../../utilities/formValidation";
import {changePassword} from "../../store/actions/userActions";
import {getProfile} from "../../store/actions/profileActions";
import Info from "../elements/Info/Info";

const ChangePassword = () => {
    document.title = 'Change Password | Invoice App';
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const changePasswordErrors = useSelector((state) => state.errors.changePassword);
    const {profile} = useSelector((state) => state.profile);
    const [formData, setFormData] = useState({password: '', newPassword: '', confirmPassword: ''});
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const handleInputChange = (event) => {
        const {name, value} = event.target;
        setFormData((prevState) => ({...prevState, [name]: value}));
    };

    const handleChangePassword = async (event) => {
        event.preventDefault();
        const validationErrors = formValidation(formData);
        if (validationErrors.length > 0) {
            dispatch(setErrors('changePassword', {}, validationErrors));
            return;
        }

        if (formData.newPassword !== formData.confirmPassword) {
            dispatch(setErrors('changePassword', 'Passwords do not match', ['Passwords do not match']));
            return;
        }

        const data = {
            ...formData,
            id: profile.id,
        }

        try {
            const response = await changePasswordAPI(data);
            dispatch(changePassword(response.data));
            if (response.status === 200) {
                toast.success("Password changed successfully");
                sessionStorage.removeItem('authTokenData');
                navigate('/login');
            }
        } catch (error) {
            dispatch(setErrors(
                'changePassword',
                error.response.data.errors || {},
                [error.response.data.message || 'Failed to change password. Please try again.']
            ));
            toast.error(error.response.data.message || 'Failed to change password.');
        }
    };

    useEffect(() => {
        if (!profile) {
            dispatch(getProfile());
        }
    }, []);

    return (
        <motion.div className={changePasswordStyles.changePasswordBg}>
            <motion.div
                className={changePasswordStyles.shape1}
                animate={{
                    scale: [1, 1.15, 1],
                    rotate: [0, 360],
                }}
                transition={{
                    duration: 10,
                    repeat: Infinity,
                    ease: "linear",
                }}
            />
            <motion.div
                className={changePasswordStyles.shape2}
                animate={{
                    scale: [1, 1.15, 1],
                    rotate: [0, -360],
                }}
                transition={{
                    duration: 10,
                    repeat: Infinity,
                    ease: "linear",
                }}
            />
            <motion.div
                initial={{opacity: 0}}
                animate={{opacity: 1}}
                transition={{
                    duration: 1,
                    ease: "easeOut",
                    bounce: 0.3,
                }}
                className={changePasswordStyles.formContainer}>
                <h2 className={styles.title}>
                    Change Your Password
                </h2>
                <Info paragraphs={[
                    'Account may contain sensitive information. ',
                    'Make sure password is secure',
                    'Password must be at least 8 characters long',
                    'And includes a number and a special character'
                ]}
                />
                <form onSubmit={handleChangePassword}>
                    <div className={changePasswordStyles.inputGroup}>
                        <div className={styles.inputContainer}>
                            <label
                                htmlFor="password"
                                className={clsx(styles.label, {
                                    [styles.error]: changePasswordErrors.password,
                                })}
                            >
                                Old Password
                                {changePasswordErrors.password && (
                                    <span className={styles.error}>Can't be empty</span>
                                )}
                            </label>
                            <input
                                className={clsx(styles.input, {
                                    [styles.error]: changePasswordErrors.password
                                })}
                                type="password"
                                name="password"
                                value={formData.password}
                                placeholder="Enter your password"
                                onChange={handleInputChange}
                            />
                        </div>
                        <div className={styles.inputContainer}>
                            <label
                                htmlFor="newPassword"
                                className={clsx(styles.label, {
                                    [styles.error]: changePasswordErrors.newPassword,
                                })}
                            >
                                New Password
                            </label>
                            <div
                                className={changePasswordStyles.passwordInputContainer}> {/* Custom container for input + icon */}
                                <input
                                    className={styles.input}
                                    type={showNewPassword ? "text" : "password"} // Toggle password visibility
                                    name="newPassword"
                                    value={formData.newPassword}
                                    placeholder="Enter your new password"
                                    onChange={handleInputChange}
                                />
                                <span
                                    className={changePasswordStyles.eyeIcon} // Eye icon style
                                    onClick={() => setShowNewPassword(!showNewPassword)} // Toggle visibility
                                >
                                    {showNewPassword ? <AiOutlineEyeInvisible/> : <AiOutlineEye/>}
                                </span>
                            </div>
                        </div>
                        <div className={styles.inputContainer}>
                            <label
                                htmlFor="confirmPassword"
                                className={clsx(styles.label, {
                                    [styles.error]: changePasswordErrors.confirmPassword,
                                })}
                            >
                                Confirm New Password
                            </label>
                            <div className={changePasswordStyles.passwordInputContainer}>
                                <input
                                    className={styles.input}
                                    type={showConfirmPassword ? "text" : "password"}
                                    name="confirmPassword"
                                    value={formData.confirmPassword}
                                    placeholder="Confirm your new password"
                                    onChange={handleInputChange}
                                />
                                <span
                                    className={changePasswordStyles.eyeIcon}
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                >
                                    {showConfirmPassword ? <AiOutlineEyeInvisible/> : <AiOutlineEye/>}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div className={changePasswordStyles.buttonContainer}>
                        <Button className='primary' type="submit">Change Password</Button>
                    </div>
                    {changePasswordErrors.msg.length > 0 && (
                        <div className={changePasswordStyles.errorContainer}>
                            {changePasswordErrors.msg.map((item, index) => (
                                <span key={index} className={styles.error}>{item}</span>
                            ))}
                        </div>
                    )}
                </form>
            </motion.div>
        </motion.div>
    );
};

export default ChangePassword;
